import type { LanguageCode } from '@/i18n/languages';

/**
 * V177: Invitation Template System
 * 
 * This module provides:
 * 1. INVITE_MESSAGE_WHATSAPP constant (source of truth for code-level fallback)
 * 2. renderInviteMessage() function (renders template with invite link)
 * 
 * Architecture:
 * - DB (invitation_templates table) = primary source of truth
 * - Code constant = fallback + seeder for DB
 * - Template uses {invite_link} placeholder
 */

/**
 * Default WhatsApp invitation message template
 * 
 * This is used as:
 * 1. Fallback when DB template is unavailable
 * 2. Seeder for the default DB template
 * 3. Reference for message format
 * 
 * IMPORTANT: Keep this in sync with the 'whatsapp_default' template in the database
 */
export const INVITE_TEMPLATE_KEY_BY_LANGUAGE = {
  en: 'whatsapp_default',
  he: 'whatsapp_default_he',
  fr: 'whatsapp_default_fr',
  de: 'whatsapp_default_de',
  es: 'whatsapp_default_es',
} as const satisfies Record<LanguageCode, string>;

export const ENGLISH_INVITE_TEMPLATE_KEY = 'whatsapp_default' as const;

export function resolveInviteTemplateKey(
  languageCode: LanguageCode | null | undefined
): string {
  if (languageCode && languageCode in INVITE_TEMPLATE_KEY_BY_LANGUAGE) {
    return INVITE_TEMPLATE_KEY_BY_LANGUAGE[languageCode];
  }
  return ENGLISH_INVITE_TEMPLATE_KEY;
}

export const INVITE_MESSAGE_WHATSAPP = `\u{1F440} Check this out

I just joined BlissNet.

Meet people through mutual friends.

\u{1F447} Join me:
{invite_link}`.trim();

/**
 * Render an invitation message by replacing the {invite_link} placeholder
 * 
 * @param params.inviteLink - The full invite URL (e.g., https://blissnet.app/invite/abc123)
 * @param params.templateBody - Optional template body from DB (uses fallback if null/empty)
 * @returns Fully rendered invitation message ready to share
 * 
 * @example
 * ```ts
 * const message = renderInviteMessage({
 *   inviteLink: 'https://blissnet.app/invite/abc123',
 *   templateBody: null // Will use INVITE_MESSAGE_WHATSAPP fallback
 * });
 * ```
 */
export function renderInviteMessage(params: {
  inviteLink: string;
  templateBody?: string | null;
}): string {
  console.log('🎨 V177: Rendering invite message', {
    hasTemplateBody: !!params.templateBody,
    inviteLink: params.inviteLink
  });

  // Use DB template if available and non-empty, otherwise use code constant
  const body =
    params.templateBody && params.templateBody.trim().length > 0
      ? params.templateBody
      : INVITE_MESSAGE_WHATSAPP;

  // Replace placeholder with actual invite link
  const rendered = body.replace('{invite_link}', params.inviteLink);

  console.log('✅ V177: Message rendered', {
    usedFallback: !params.templateBody,
    length: rendered.length
  });

  return rendered;
}

/**
 * Build the full invite link URL from an invite code
 * 
 * @param inviteCode - The unique invite code
 * @returns Full invite URL
 */
export function buildInviteLink(inviteCode: string): string {
  return `https://blissnet.app/invite/${inviteCode}`;
}

/**
 * Validate that a template body contains the required placeholder
 * 
 * @param templateBody - Template body to validate
 * @returns true if valid, false otherwise
 */
export function validateTemplateBody(templateBody: string): boolean {
  return templateBody.includes('{invite_link}');
}

